import Box from '@mui/material/Box';
import { alpha, Theme, useTheme } from '@mui/material/styles';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { IEdition } from 'src/interfaces/Edition';
import { buildRevenueChartData } from 'src/pages/Dashboard/Editions/utils/editionAccounting';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

interface Props {
  editions: IEdition[];
  selectedEditionId?: string | null;
}

const getBarColors = (
  editionIds: string[],
  selectedEditionId: string | null | undefined,
  palette: Theme['palette'],
) => {
  const series = [
    palette.primary.main,
    palette.secondary.main,
    palette.success.main,
    palette.warning.main,
  ];

  return editionIds.map((id, index) => {
    const color = series[index % series.length] ?? palette.primary.main;
    if (id === selectedEditionId) {
      return color;
    }
    return alpha(color, 0.72);
  });
};

const EditionRevenueChart = ({ editions, selectedEditionId }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { labels, values, editionIds } = useMemo(
    () => buildRevenueChartData(editions),
    [editions],
  );

  const backgroundColor = useMemo(
    () => getBarColors(editionIds, selectedEditionId, theme.palette),
    [editionIds, selectedEditionId, theme.palette],
  );

  const borderColor = useMemo(
    () =>
      editionIds.map(id =>
        id === selectedEditionId ? theme.palette.text.primary : 'transparent',
      ),
    [editionIds, selectedEditionId, theme.palette.text.primary],
  );

  if (labels.length === 0) {
    return null;
  }

  const axisColor = theme.palette.text.secondary;
  const gridColor = alpha(theme.palette.text.primary, 0.12);

  return (
    <Box sx={{ height: 280, width: '100%' }}>
      <Bar
        options={{
          maintainAspectRatio: false,
          responsive: true,
          plugins: {
            legend: { display: false },
            tooltip: {
              displayColors: false,
              callbacks: {
                label: ({ parsed }) =>
                  t('base.currencyValue', { count: parsed.y ?? 0 }),
              },
            },
          },
          scales: {
            x: {
              ticks: { color: axisColor },
              grid: { color: gridColor },
            },
            y: {
              beginAtZero: true,
              ticks: {
                color: axisColor,
                callback: value => t('base.currencyValue', { count: Number(value) }),
              },
              grid: { color: gridColor },
            },
          },
        }}
        data={{
          labels,
          datasets: [
            {
              label: t('editions.accounting.chartTitle'),
              data: values,
              backgroundColor,
              borderColor,
              borderWidth: 2,
              borderRadius: 4,
            },
          ],
        }}
      />
    </Box>
  );
};

export default EditionRevenueChart;
